/**
 * Auto-generated
 * Get a sortable value for a size string
 * Returns an object with { value: number, type: string } for sorting
 */
function getSizeSortValue(size) {
  const lower = size.toLowerCase().trim();

  // Special cases that should come first
  if (lower === 'newborn') {
    return { value: -1, type: 'newborn' }; // Use -1 to ensure it comes before all month-based sizes (which start at 0)
  }

  // Special cases that should come last
  if (lower.includes('one size') || lower === 'standard') {
    return { value: Infinity, type: 'universal' };
  }

  // Bedding/household sizes (should come after clothing)
  if (lower === 'twin') {
    return { value: 10000, type: 'bedding' };
  }
  if (
    lower.includes('full') ||
    lower.includes('queen') ||
    lower.includes('king')
  ) {
    return { value: 10001, type: 'bedding' };
  }

  // Months format: "0-3 months", "3-6 months", etc.
  const monthsMatch = size.match(/(\d+)\s*-\s*(\d+)\s*months?/i);
  if (monthsMatch) {
    const start = parseInt(monthsMatch[1], 10);
    const end = parseInt(monthsMatch[2], 10);
    return { value: start, end: end, type: 'months' };
  }

  // Single month: "3 months" (if exists)
  const singleMonthMatch = size.match(/(\d+)\s*months?/i);
  if (singleMonthMatch) {
    const month = parseInt(singleMonthMatch[1], 10);
    return { value: month, type: 'months' };
  }

  // Years format: "7Y-8Y", "10Y-12Y", etc.
  const yearsMatch = size.match(/(\d+)\s*Y\s*-\s*(\d+)\s*Y/i);
  if (yearsMatch) {
    const startYears = parseInt(yearsMatch[1], 10);
    const endYears = parseInt(yearsMatch[2], 10);
    return { value: startYears * 12, end: endYears * 12, type: 'years' }; // Convert to months
  }

  // Single year: "7Y" (if exists)
  const singleYearMatch = size.match(/(\d+)\s*Y/i);
  if (singleYearMatch) {
    const year = parseInt(singleYearMatch[1], 10);
    return { value: year * 12, type: 'years' };
  }

  // T sizes (Toddler): "2T", "3T", "3T-4T", etc.
  // T sizes roughly correspond to age in years (2T = 2 years, 3T = 3 years, etc.)
  const tSizeMatch = size.match(/(\d+)\s*T/i);
  if (tSizeMatch) {
    const tAge = parseInt(tSizeMatch[1], 10);
    return { value: tAge * 12, type: 'toddler' }; // Convert to months
  }

  // Letter sizes with numeric ranges: "Small 0-2", "Large 8-10", etc.
  const letterWithRangeMatch = size.match(
    /(xs|small|medium|large|xl)\s+(\d+)\s*-\s*(\d+)/i
  );
  if (letterWithRangeMatch) {
    const start = parseInt(letterWithRangeMatch[2], 10);
    return { value: start + 200, type: 'letter-numeric' }; // Offset to come after months/years
  }

  // Letter sizes: XS, S, M, L, XL, 2XL, 3XL, etc.
  const letterSizeOrder = {
    xxs: 300,
    xs: 301,
    s: 302,
    m: 303,
    l: 304,
    xl: 305,
    '2xl': 306,
    '3xl': 307,
  };

  // Check for 2XL, 3XL first (before single XL)
  if (lower.match(/^3xl/)) {
    return { value: 307, type: 'letter' };
  }
  if (lower.match(/^2xl/)) {
    return { value: 306, type: 'letter' };
  }

  // Check other letter sizes
  for (const [key, val] of Object.entries(letterSizeOrder)) {
    if (lower.startsWith(key)) {
      return { value: val, type: 'letter' };
    }
  }

  // If no pattern matches, sort alphabetically at the end (before universal sizes)
  return { value: 5000, type: 'unknown', original: size };
}

/**
 * Sort sizes by their sort value
 *
 * @param {string[]} sizes
 * @returns
 */
export function algoliaSortSizes(sizes) {
  return sizes.slice().sort((a, b) => {
    const aParsed = getSizeSortValue(a.label);
    const bParsed = getSizeSortValue(b.label);

    // First compare by value (start value)
    if (aParsed.value !== bParsed.value) {
      return aParsed.value - bParsed.value;
    }

    // If start values are equal, compare by type first (for special cases like "Newborn")
    const typeOrder = {
      newborn: 0,
      months: 1,
      years: 2,
      toddler: 3,
      'letter-numeric': 4,
      letter: 5,
      bedding: 6,
      unknown: 7,
      universal: 8,
    };
    const aTypeOrder = typeOrder[aParsed.type] || 9;
    const bTypeOrder = typeOrder[bParsed.type] || 9;

    if (aTypeOrder !== bTypeOrder) {
      return aTypeOrder - bTypeOrder;
    }

    // If same type and start values are equal, and both have end values, compare by end value
    if (aParsed.end !== undefined && bParsed.end !== undefined) {
      if (aParsed.end !== bParsed.end) {
        return aParsed.end - bParsed.end;
      }
    }

    // If same type and value, sort alphabetically
    return a.label.localeCompare(b.label);
  });
}

/**
 * Debounce a promise function to prevent multiple calls to the same function within a certain time frame
 *
 * @param {Function} fn
 * @param {number} time
 * @returns
 */
export function debouncePromise(fn, time) {
  let timer = undefined;

  return function debounced(...args) {
    if (timer) {
      clearTimeout(timer); // Clear the timeout first if it's already defined.
    }

    return new Promise((resolve) => {
      timer = setTimeout(() => resolve(fn(...args)), time);
    });
  };
}
